import { queryCache, useQuery } from "react-query"

import { BillingType } from "routes/Base/Admin/MarketSettings/List/BillingTypes/types"
import { BillingTypeFilter } from "models/bookingOrder"
import { CellProps } from "components/TreeState"
import { checkBuyingStatus } from "utils/type-guards"
import { ListCell } from "./ListCell"
import { fetchBillingTypes } from "api/billingtypes"
import { get } from "lodash"
import { useDefaultFieldId } from "../useDefaultFieldId"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"

export type BillingTypeTuple = {
  billingTypeId: string
  billingSubtypeId?: string
}

const TUPLE_ID_SEPARATOR = "/"

export function BillingTypeCell(props: CellProps) {
  const {
    rowId: id,
    dataKey: dk,
    disabled,
    primaryKey,
    secondaryKey,
    id: fieldId,
    semantic,
  } = props
  const tableSource = useTableSource()
  const defaultFieldId = useDefaultFieldId(id, semantic)
  const dataKey = dk || `${primaryKey}.${defaultFieldId}`
  const readFrom = useSnapshot(tableSource.state)

  const billingTypeTuple = get(readFrom[id], dataKey) as BillingTypeTuple
  const secondaryTuple = get(
    readFrom[id],
    `${secondaryKey}.${fieldId}`,
  ) as BillingTypeTuple

  const { data: billingTypes } = useQuery(
    ["billing-types"],
    fetchBillingTypes,
    {
      initialData: queryCache.getQueryData(["billing-types"]),
      refetchOnMount: !queryCache.getQueryData(["billing-types"]),
    },
  )

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  const options = getBillingTypeOptions(billingTypes)

  const selected = !!billingTypeTuple
    ? billingTypeTupleToString(billingTypeTuple)
    : undefined

  const secondaryValue = secondaryTuple
    ? options?.find((o) => o.value === billingTypeTupleToString(secondaryTuple))
        ?.label
    : undefined

  const isDisabled = buyingStatus === "POSTED_FOR_BILLING"

  return (
    <ListCell
      {...props}
      options={options}
      selected={selected}
      secondary={secondaryValue}
      normalize={normalizeBillingType}
      disabled={!!disabled || isDisabled}
      dataKey={dataKey}
    />
  )
}

export function getBillingTypeOptions(
  billingTypes?: BillingType[],
  available?: BillingTypeFilter[],
) {
  if (!billingTypes?.length) return []

  const options: Array<{ value: string; label: string; disabled?: boolean }> =
    []

  billingTypes?.forEach((bt) => {
    const single = { value: bt.id, label: bt.name }
    const isAvailable = !!available
      ? available.some((a) => a.billingTypeId === bt.id && !a.billingSubtypeId)
      : true
    isAvailable && options.push(single)

    bt.billingSubtypes?.forEach((sbt) => {
      let valid = true
      if (!!available) {
        valid = available?.some(
          (a) => a.billingTypeId === bt.id && a.billingSubtypeId === sbt.id,
        )
      }
      const value = [bt.id, sbt.id].join(TUPLE_ID_SEPARATOR)
      const label = [bt.name, sbt.name].join(` ${TUPLE_ID_SEPARATOR} `)
      const disabled = sbt.status !== "ACTIVE"

      valid && options.push({ value, label, disabled })
    })
  })

  return options
}

export function billingTypeTupleToString(btt: BillingTypeTuple) {
  if (!btt.billingSubtypeId) return btt.billingTypeId
  return [btt.billingTypeId, btt.billingSubtypeId].join(TUPLE_ID_SEPARATOR)
}

export function normalizeBillingType(stringId: string): BillingTypeTuple {
  const [billingTypeId, billingSubtypeId] = stringId.split(TUPLE_ID_SEPARATOR)
  return { billingTypeId, billingSubtypeId }
}
